import { ElementType } from "react";
import { Box, Card, CardContent, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { HelperSummary } from "@my-helpers/common";
import { useNavigate } from "react-router-dom";
import { Delete } from "@mui/icons-material";
import { formatDate } from "@/utils/dateHelpers";
import { Button } from "@/components/Button";

interface HelperSummaryProps {
    helper: HelperSummary;
    setDeleting: (id: string) => void;
}

export const HelperSummaryCard: ElementType<HelperSummaryProps> = ({ helper, setDeleting }) => {
    const navigate = useNavigate();

    const getStatusColor = (status: string) => {
        return status === 'active' ? 'success' : 'default';
    };

    return (
        <Card
            onClick={() => navigate(`/helpers/${helper.id}`)}
            sx={{
                height: '100%',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                }
            }}
        >
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1,
                    mb: 2
                }}>
                    <Typography variant="h6" component="h2" noWrap>
                        {helper.name}
                    </Typography>
                    <Chip
                        label={`${helper.training.name}:${helper.training.version}`}
                        color="default"
                        size="small"
                        variant="outlined"
                    />
                </Box>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minHeight: '2.5em', // Ensure 2 lines of height
                    }}
                >
                    {helper.description}
                </Typography>

                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    mb: 1
                }}>
                    <Chip
                        label={helper.status}
                        color={getStatusColor(helper.status)}
                        size="small"
                    />
                    {
                        helper.system_roles && helper.system_roles.map((role: string) => (
                            <Chip
                                label={role}
                                color="warning"
                                size="small"
                                variant="filled"
                            />
                        ))
                    }
                    {
                        helper.tagged_by && helper.tagged_by.map((tag: string) => (
                            <Chip
                                label={tag}
                                color="default"
                                size="small"
                                variant="outlined"
                            />
                        ))
                    }
                </Box>

                <Box
                    display="flex"
                    flexDirection="row"
                    flexWrap="wrap"
                    alignItems="center"
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        flex={1}
                    >
                        Last updated: {formatDate(helper.updated_at)}
                    </Typography>
                    {
                        helper.system_roles.length === 0 && (
                            <Button
                                variant="error"
                                size="small"
                                icon={<Delete />}
                                tooltip="Delete helper"
                                onClick={(e) => {
                                    setDeleting(helper.id);
                                    e.stopPropagation();
                                }}
                            />
                        )
                    }
                </Box>
            </CardContent>
        </Card>
    );
};